import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { compare, Operation } from 'fast-json-patch';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import moment from 'moment';
import { MachineApiService } from '../../../core/services/api/machine-api.service';
import { EditablePostClientStatus } from '../../../core/enums/editable-post-client-status';
import { MachineOwnerType } from '../../../core/enums/machine-owner-type';
import { ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../core/enums/production-technician-area';
import { WindowsVersion } from '../../../core/enums/windows-version';
import { MachineDetailsDto } from '../../../core/models/machines/machine-details-dto';
import { MachineDetailsTeamInfoDto } from '../../../core/models/machines/machine-details-team-info-dto';
import { MachineDetailsForm } from '../../../core/models/machines/machine-form-models';
import { MachineStatus } from '../../../core/models/machines/machine-status';
import { CreateMachineModel } from '../components/create-machine-dialog/create-machine-dialog.component';
import { MachineWorkHoursService } from './machine-work-hours.service';
import { UpdateMachineModel } from '../../../core/models/machines/update-machine-model';
import { MachineOwnerService } from './machine-owner.service';

@Injectable({
    providedIn: 'root'
})
export class MachineDetailsService {

    constructor(private workHoursService: MachineWorkHoursService, private machineApiService: MachineApiService, private machineOwnerService: MachineOwnerService) { }

    public createNewMachine(dialogResult: CreateMachineModel): MachineDetailsDto {
        return {
            id: 0,
            baseInfo: {
                machineManufacturerId: dialogResult.machineManufacturerId,
                machineManufacturerModelId: dialogResult.machineManufacturerModelId,
                machineType: dialogResult.machineType,
                ownerType: dialogResult.ownerType,
                machineId: '',
                machineManufacturerText: '',
                machineManufacturerModelText: '',
                machineTypeText: '',
                registrationNumber: '',
                manufacturingNumber: '',
                machineSizeText: dialogResult.machineSizeText,
                machineOwnerId: dialogResult.machineOwnerId ?? 0,
                machineOwnerText: '',
                machineModelText: '',
                machineModelYear: 0,
                status: MachineStatus.Active,
                statusText: '',
                ownerTypeText: '',
                otherEquipment: '',
                isSchoolForwarder: false,
                isGrotForwarder: false,
                isDemoMachine: false,
                teams: [],
                created: '',
                lastModified: '',
                entityRelationChangeTransactionDetails: null
            },
            notes: '',
            biometriaInfo: {
                biometriaCompanyId: '',
                biometriaGpxId: 'sdcgpx',
                biometriaPassword: ''
            },
            productionInfo: {
                harvesterHeadId: 0,
                productionBasePerformanceM3FubPerHour: 0,
                fuelConsumptionInLitersPerG5h: 0,
                oilConsumptionInLitersPerG5h: 0,
                numberOfWheels: 0,
                tireDimension: '',
                loadingCapacity: 0,
                craneLength: 0
            },
            equipmentInfo: {
                machineComputerId: 0,
                machineComputerText: '',
                stanford2010Version: null,
                stinaVersion: '',
                windowsVersion: '',
                stanford2010VersionText: '',
                hasHighPrecisionCraneTipCoordinate: false,
                hasRtk: false,
                masenVersion: '',
                fileSendingActive: false,
                sendPinFilesForPulp: false,
                sendAptFilesViaMasen: false,
                useProductionFilesFromMasen: false
            },
            thinningPerformance: [],
            regenerationPerformance: [],
            defaultMachinePerformanceArea: null,
            machineWorkHours: [
                {
                    clientStatus: EditablePostClientStatus.New,
                    from: moment().startOf('day').toISOString(),
                    hoursPerDay: 12
                }
            ]
        };
    }

    public createUpdateModel(machine: MachineDetailsDto, form?: FormGroup<MachineDetailsForm>): UpdateMachineModel {
        const formValues = form?.getRawValue();
        const baseForm = formValues?.baseInfo;
        const productionForm = formValues?.productionInfo;
        const equipmentForm = formValues?.equipmentInfo;
        const biometriaForm = formValues?.biometriaInfo;
        return {
            id: machine.id,
            machineType: baseForm ? baseForm.machineType : machine.baseInfo.machineType,
            machineId: (baseForm ? baseForm.machineId : machine.baseInfo.machineId) ?? '',
            ownerType: baseForm ? baseForm.ownerType : machine.baseInfo.ownerType,
            machineManufacturerId: baseForm ? baseForm.machineManufacturerId : machine.baseInfo.machineManufacturerId,
            registrationNumber: baseForm ? baseForm.registrationNumber : machine.baseInfo.registrationNumber,
            manufacturingNumber: baseForm ? baseForm.manufacturingNumber : machine.baseInfo.manufacturingNumber,
            modelYear: baseForm ? baseForm.machineModelYear : machine.baseInfo.machineModelYear,
            machineManufacturerModelId: baseForm ? baseForm.machineManufacturerModelId : machine.baseInfo.machineManufacturerModelId,
            status: baseForm ? baseForm.status : machine.baseInfo.status,
            machineOwnerId: baseForm ? baseForm.machineOwnerId : machine.baseInfo.machineOwnerId,
            otherEquipment: baseForm ? baseForm.otherEquipment : machine.baseInfo.otherEquipment,
            isSchoolForwarder: baseForm ? baseForm.isSchoolForwarder : machine.baseInfo.isSchoolForwarder,
            isGrotForwarder: baseForm ? baseForm.isGrotForwarder : machine.baseInfo.isGrotForwarder,
            isDemoMachine: baseForm ? baseForm.isDemoMachine : machine.baseInfo.isDemoMachine,
            notes: baseForm ? (baseForm.notes ?? '') : machine.notes,
            harvesterHeadId: productionForm ? productionForm.harvesterHeadId : machine.productionInfo.harvesterHeadId,
            basePerformance: productionForm ? productionForm.productionBasePerformanceM3FubPerHour : machine.productionInfo.productionBasePerformanceM3FubPerHour,
            fuelConsumptionInLitersPerG5Hour: productionForm ? productionForm.fuelConsumptionInLitersPerG5h : machine.productionInfo.fuelConsumptionInLitersPerG5h,
            oilConsumptionInLitersPerG5Hour: productionForm ? productionForm.oilConsumptionInLitersPerG5h : machine.productionInfo.oilConsumptionInLitersPerG5h,
            numberOfWheels: productionForm ? productionForm.numberOfWheels : machine.productionInfo.numberOfWheels,
            tireDimension: productionForm ? productionForm.tireDimension : machine.productionInfo.tireDimension,
            loadingCapacity: productionForm ? productionForm.loadingCapacity : machine.productionInfo.loadingCapacity,
            craneLength: productionForm ? productionForm.craneLength : machine.productionInfo.craneLength,
            machineComputerId: equipmentForm ? equipmentForm.machineComputerId : machine.equipmentInfo.machineComputerId,
            stanford2010Version: equipmentForm ? equipmentForm.stanford2010Version : machine.equipmentInfo.stanford2010Version,
            stinaVersion: equipmentForm ? equipmentForm.stinaVersion : machine.equipmentInfo.stinaVersion,
            windowsVersion: (equipmentForm ? equipmentForm.windowsVersion : machine.equipmentInfo.windowsVersion) as WindowsVersion | null | undefined,
            hasHighPrecisionCraneTipCoordinate: equipmentForm ? Boolean(equipmentForm.hasHighPrecisionCraneTipCoordinate) : Boolean(machine.equipmentInfo.hasHighPrecisionCraneTipCoordinate),
            hasRtk: equipmentForm ? Boolean(equipmentForm.hasRtk) : Boolean(machine.equipmentInfo.hasRtk),
            fileSendingActive: equipmentForm ? equipmentForm.fileSendingActive : machine.equipmentInfo.fileSendingActive,
            sendPinFilesForPulp: equipmentForm ? equipmentForm.sendPinFilesForPulp : machine.equipmentInfo.sendPinFilesForPulp,
            sendAptFilesViaMasen: equipmentForm ? equipmentForm.sendAptFilesViaMasen : machine.equipmentInfo.sendAptFilesViaMasen,
            useProductionFilesFromMasen: equipmentForm ? equipmentForm.useProductionFilesFromMasen : machine.equipmentInfo.useProductionFilesFromMasen,
            biometriaCompanyId: biometriaForm ? biometriaForm.biometriaCompanyId : machine.biometriaInfo.biometriaCompanyId,
            biometriaGpxId: biometriaForm ? biometriaForm.biometriaGpxId : machine.biometriaInfo.biometriaGpxId,
            biometriaPassword: biometriaForm ? biometriaForm.biometriaPassword : machine.biometriaInfo.biometriaPassword,
            thinningPerformance: machine.thinningPerformance.filter(x => x.clientStatus != undefined),
            regenerationPerformance: machine.regenerationPerformance.filter(x => x.clientStatus != undefined),
            machineWorkHours: this.workHoursService.mapForUpdate(machine.machineWorkHours),
            defaultMachinePerformanceArea: machine.defaultMachinePerformanceArea,
            addedTeams: this.getTeamIdsByStatus(machine, EditablePostClientStatus.New),
            removedTeams: this.getTeamIdsByStatus(machine, EditablePostClientStatus.Deleted),
        }
    }

    public createPatchOperations(baseline: UpdateMachineModel, current: UpdateMachineModel): { patch: Operation[]; current: UpdateMachineModel } {
        const patch = compare(baseline, current);
        return { patch, current };
    }

    public normalizePatchOperations(patch: Operation[], current: UpdateMachineModel): Operation[] {
        if (!patch?.length) {
            return patch;
        }

        const arrayRoots = [
            '/regenerationPerformance',
            '/thinningPerformance',
            '/addedTeams'
        ];

        const hasMachineWorkHoursChange = patch.some(op => op.path.startsWith('/machineWorkHours'));
        let normalizedPatch = patch;

        if (hasMachineWorkHoursChange) {
            normalizedPatch = normalizedPatch.filter(op => !op.path.startsWith('/machineWorkHours'));
            normalizedPatch.push({
                op: 'replace',
                path: '/machineWorkHours',
                value: current.machineWorkHours
            });
        }

        return normalizedPatch.map(op => {
            if (op.op === 'add') {
                const root = arrayRoots.find(r => op.path.startsWith(r + '/'));
                if (root && !op.path.endsWith('/-')) {
                    return { ...op, path: `${root}/-` };
                }
            }

            return op;
        });
    }

    public applyUpdateModelToMachine(machine: MachineDetailsDto, update: UpdateMachineModel): void {
        if (!machine) {
            return;
        }

        if (update.id != null) {
            machine.id = update.id;
        }

        if (update.machineId) {
            machine.baseInfo = {
                ...machine.baseInfo,
                machineId: update.machineId
            };
        }

        if (update.ownerType !== undefined) {
            machine.baseInfo.ownerType = update.ownerType;
        }

        if (update.machineOwnerId !== undefined) {
            machine.baseInfo.machineOwnerId = update.machineOwnerId;
        }

        if (update.machineManufacturerId !== undefined) {
            machine.baseInfo.machineManufacturerId = update.machineManufacturerId;
        }

        if (update.machineManufacturerModelId !== undefined) {
            machine.baseInfo.machineManufacturerModelId = update.machineManufacturerModelId;
        }

        if (update.notes !== undefined) {
            const normalizedNotes = update.notes ?? '';
            machine.notes = normalizedNotes;
        }

        if (update.harvesterHeadId !== undefined) {
            machine.productionInfo.harvesterHeadId = update.harvesterHeadId;
        }

        if (update.machineComputerId !== undefined) {
            machine.equipmentInfo.machineComputerId = update.machineComputerId;
        }

        if (update.machineWorkHours) {
            machine.machineWorkHours = update.machineWorkHours.map(workHour => ({ ...workHour }));
        }

        if (update.thinningPerformance) {
            machine.thinningPerformance = update.thinningPerformance.map(performance => ({ ...performance }));
        }

        if (update.regenerationPerformance) {
            machine.regenerationPerformance = update.regenerationPerformance.map(performance => ({ ...performance }));
        }

        if (update.defaultMachinePerformanceArea !== undefined) {
            machine.defaultMachinePerformanceArea = update.defaultMachinePerformanceArea;
        }

        this.applyTeamChangesAfterSave(machine, update);
    }

    public applyTeamChangesAfterSave(machine: MachineDetailsDto, update: UpdateMachineModel): void {
        if (!machine?.baseInfo) {
            return;
        }

        const additions = new Set(update.addedTeams ?? []);
        const removals = new Set(update.removedTeams ?? []);

        if (additions.size === 0 && removals.size === 0) {
            return;
        }

        const currentTeams = machine.baseInfo.teams ?? [];
        let updatedTeams: MachineDetailsTeamInfoDto[] = [...currentTeams];

        if (removals.size > 0) {
            updatedTeams = updatedTeams.filter(team => !removals.has(team.id));
        }

        if (additions.size > 0) {
            updatedTeams = updatedTeams.map(team => additions.has(team.id) ? { ...team, clientStatus: undefined } : team);
        }

        machine.baseInfo.teams = updatedTeams;

        update.addedTeams = [];
        update.removedTeams = [];
    }

    public determineSwitchedTechnicianArea(machine: MachineDetailsDto, teams: MachineDetailsTeamInfoDto[]): string {
        const currentAreaEnum: ProductionTechnicianArea = machine.defaultMachinePerformanceArea ?? ProductionTechnicianArea.Unknown;
        const currentTeams = machine.baseInfo.teams.filter(t => t.productionTechnicianArea === ProductionTechnicianAreaLabels[currentAreaEnum] &&
            t?.clientStatus !== EditablePostClientStatus.Deleted);
        const deletedTeams = teams.filter(t => t.clientStatus === EditablePostClientStatus.Deleted);

        const currentAreaIsUnknownAndAllLegacyTeamsAreDeleted = currentAreaEnum === ProductionTechnicianArea.Unknown &&
            machine.baseInfo.teams.filter(t => t.clientStatus !== EditablePostClientStatus.New).
                every(t => deletedTeams.some(deletedTeam => deletedTeam.teamId === t.teamId) || t.clientStatus === EditablePostClientStatus.Deleted);
        if (currentAreaEnum === ProductionTechnicianArea.Unknown && !currentAreaIsUnknownAndAllLegacyTeamsAreDeleted) {
            return "";
        }

        const allCurrentTeamsAreDeleted = currentTeams.every(t => deletedTeams.some(deletedTeam => deletedTeam.teamId === t.teamId));
        if (currentAreaIsUnknownAndAllLegacyTeamsAreDeleted || allCurrentTeamsAreDeleted) {
            const newTeams = teams.filter(t => t.clientStatus === EditablePostClientStatus.New);
            const otherTeams = machine.baseInfo.teams.filter(t => t.productionTechnicianArea !== ProductionTechnicianAreaLabels[currentAreaEnum] &&
                t?.clientStatus !== EditablePostClientStatus.Deleted);

            return newTeams.find(t => !deletedTeams.some(deletedTeam => deletedTeam.teamId === t.teamId))?.productionTechnicianArea ||
                otherTeams.find(t => !deletedTeams.some(deletedTeam => deletedTeam.teamId === t.teamId))?.productionTechnicianArea || '';
        }

        return '';
    }

    public resetTeamStateAfterSave(machine: MachineDetailsDto, currentModel: UpdateMachineModel): void {
        currentModel.addedTeams = [];
        currentModel.removedTeams = [];

        if (!machine?.baseInfo?.teams) {
            return;
        }

        machine.baseInfo.teams = machine.baseInfo.teams
            .filter(team => team.clientStatus !== EditablePostClientStatus.Deleted)
            .map(team => ({ ...team, clientStatus: undefined }));
    }

    public conditionalContractorValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.parent) return null;
            const ownerType = control.parent?.get('ownerType')?.value;

            if (ownerType === MachineOwnerType.External && !control.value) {
                return { required: true };
            }
            return null;
        };
    }

    public biometriaCompanyIdValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return this.machineOwnerService.getBiometriaCompanyIdValueValidationErrors(control.value);
        };
    }

    public biometriaGpxIdValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const value = control.value.toString();
            const errors: ValidationErrors = {};

            if (value.length != 10 && value.length != 12) {
                errors['invalidLength'] = { actualLength: value.length, requiredLength: 10 | 12 };
            }

            if (!value.startsWith('sdcgpx')) {
                errors['invalidStart'] = { value: value };
            }

            if (!value.match(/\d{4}$/) && !value.match(/\d{6}$/)) {
                errors['invalidEnding'] = { value: value };
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    getPatternErrorMessage(errorKey: string, controlName: string, errorValue?: any): string {
        if (controlName === 'biometriaInfo.biometriaGpxId') {
            const patterns: Map<string, string> = new Map<string, string>([
                ['invalidLength', 'SDCID måste vara 10 eller 12 tecken långt'],
                ['invalidStart', 'SDCID måste börja med "sdcgpx"'],
                ['notUnique', 'SDCID används redan av en annan maskin'],
                ['invalidEnding', 'SDCID måste avslutas med siffror']
            ]);
            return patterns.get(errorKey) || 'Ogiltigt format';
        }

        if (controlName === 'biometriaInfo.biometriaCompanyId') {
            const patterns: Map<string, string> = new Map<string, string>([
                ['invalidLength', `Måste vara 10 siffror (nuvarande: ${errorValue?.actualLength})`],
                ['notOnlyNumbers', 'Måste vara endast siffror'],
                ['notEndingWithZeroes', 'Måste avslutas med fem nollor']
            ]);
            return patterns.get(errorKey) || 'Ogiltigt format';
        }

        return 'Ogiltigt format';
    }


    public biometriaGpxIdAsyncValidator(getMachineId: () => number): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value) {
                return of(null);
            }

            return timer(500).pipe(
                switchMap(() => this.machineApiService.checkGpxIdExists(control.value, getMachineId()).pipe(
                    map(isUnique => !isUnique ? { notUnique: { value: control.value } } : null)
                ))
            );
        };
    }

    private getTeamIdsByStatus(machine: MachineDetailsDto, status: EditablePostClientStatus): number[] {
        const uniqueIds = new Set<number>();

        (machine?.baseInfo?.teams ?? []).forEach(team => {
            if (team.clientStatus !== status) {
                return;
            }

            const teamId = team.id;
            if (teamId <= 0 || !Number.isFinite(teamId)) {
                return;
            }

            uniqueIds.add(teamId);
        });

        return Array.from(uniqueIds);
    }

    public mapMachineToFormValue(machine: MachineDetailsDto): ReturnType<FormGroup<MachineDetailsForm>['getRawValue']> {
        // Vi behöver denna returntypen för att formgroupen inte följer interfacet vi får från backend. Bland annat för att vi vill mappa ut lagen 
        // till en sträng och gruppera notes med baseInfo. Vi får strong typing på köpet.
        return {
            baseInfo: {
                ...machine.baseInfo,
                machineModelYear: machine.baseInfo.machineModelYear === 0 ? null : machine.baseInfo.machineModelYear,
                teams: machine.baseInfo.teams
                    .filter(t => t.clientStatus !== EditablePostClientStatus.Deleted)
                    .map(team => `${team.teamId} ${team.name}`).join('\n'),
                notes: machine.notes
            },
            biometriaInfo: machine.biometriaInfo,
            equipmentInfo: {
                ...machine.equipmentInfo,
                windowsVersion: machine.equipmentInfo.windowsVersion as WindowsVersion | null
            },
            productionInfo: {
                ...machine.productionInfo,
                productionBasePerformanceM3FubPerHour: machine.productionInfo.productionBasePerformanceM3FubPerHour === 0 ? null : machine.productionInfo.productionBasePerformanceM3FubPerHour,
                fuelConsumptionInLitersPerG5h: machine.productionInfo.fuelConsumptionInLitersPerG5h === 0 ? null : machine.productionInfo.fuelConsumptionInLitersPerG5h,
                oilConsumptionInLitersPerG5h: machine.productionInfo.oilConsumptionInLitersPerG5h === 0 ? null : machine.productionInfo.oilConsumptionInLitersPerG5h,
                numberOfWheels: machine.productionInfo.numberOfWheels === 0 ? null : machine.productionInfo.numberOfWheels,
                loadingCapacity: machine.productionInfo.loadingCapacity === 0 ? null : machine.productionInfo.loadingCapacity,
                craneLength: machine.productionInfo.craneLength === 0 ? null : machine.productionInfo.craneLength
            }
        };
    }
}
